import { logger } from '@/utils/logger';
import { env } from '@zopauk/nextjs-env';
import { launchAmazonConnectChatWidget } from '@zopauk/nextjs-amazon-connect';

export const openChat = () => {
  const amazon = document.getElementById('amazon-connect-chat-widget');
  if (amazon) {
    launchAmazonConnectChatWidget();
  }
};

export const closeChat = () => {
  try {
    // Remove all Amazon Connect related elements from the DOM
    const chatWidget = document.getElementById('amazon-connect-chat-widget');
    if (chatWidget) {
      chatWidget.remove();
    }

    // Remove any other Amazon Connect related elements
    document.querySelectorAll('[id*="amazon-connect"]').forEach((el) => el.remove());
    document.querySelectorAll('[class*="amazon-connect"]').forEach((el) => el.remove());

    // Clear Amazon Connect state from localStorage to prevent session persistence
    Object.keys(localStorage).forEach((key) => {
      if (key.toLowerCase().includes('amazon') || key.toLowerCase().includes('connect')) {
        localStorage.removeItem(key);
      }
    });

    // Clear Amazon Connect state from sessionStorage
    Object.keys(sessionStorage).forEach((key) => {
      if (key.toLowerCase().includes('amazon') || key.toLowerCase().includes('connect')) {
        sessionStorage.removeItem(key);
      }
    });

    // Reset the amazon_connect global object if it exists
    if (window.amazon_connect) {
      window.amazon_connect.ac = [];
    }

    logger.info({
      message: '[closeChat] - Amazon Connect chat widget closed and state cleared',
      id: 'amazon-connect-close-chat',
    });
  } catch (error) {
    logger.warn({
      message: '[closeChat] - Error closing Amazon Connect chat widget',
      error,
    });
  }
};

const started = Date.now();

export function waitForElementById(element: string, callback: () => void) {
  const elem = document.getElementById(element);

  if (elem) {
    setTimeout(callback.bind(null), 0);
  } else {
    if (Date.now() - started > 10000) {
      return;
    }
    setTimeout(waitForElementById.bind(null, element, callback), 0);
  }
}

export const isCI = env('NEXT_PUBLIC_IS_CI') === 'true';
export const isDev = env('NEXT_PUBLIC_APP_ENV') === 'development';
export const disableApiLogging = env('NEXT_PUBLIC_DISABLE_API_LOGGING') === 'enabled';

export const isEmptyObject = (obj: object) => {
  for (const prop in obj) {
    if (Object.hasOwn(obj, prop)) {
      return false;
    }
  }

  return true;
};

export const isExternalUrl = (url: string) => /^https?:\/\//.test(url);

export const getOrdinal = (day: number) => {
  if (day > 3 && day < 21) {
    return 'th';
  }

  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export function base64ToJson<T>(str: string): null | T {
  try {
    return JSON.parse(atob(str));
  } catch (error) {
    logger.warn({
      message: '[stringToJson] - Unable to parse string to JSON',
      error,
      str,
    });
    return null;
  }
}

export const getPercentage = (totalValue: number, value: number) => (value / totalValue) * 100;

export const slugify = (str: string) =>
  str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');

const FOCUSABLE_CSS_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), *[tabindex], *[contenteditable]';

export function findFirstFocusable(container?: Element | null) {
  return container?.querySelector<HTMLAnchorElement>(FOCUSABLE_CSS_SELECTOR);
}

export const dumbPluralize = (count: number, noun: string) => `${noun}${count !== 1 ? 's' : ''}`;

export const addCommasToNumber = (number: number | string): string => {
  const value = Number(number);

  return Number.isNaN(value) ? (number as string) : new Intl.NumberFormat('en-GB').format(value);
};

export function findDeepObjectsByKey<T>(obj: undefined | object, key: string, value: string) {
  const objs: T[] = [];

  if (!obj) {
    return objs;
  }

  try {
    JSON.stringify(obj, (_, nestedObj) => {
      if (nestedObj && nestedObj[key] === value) {
        objs.push(nestedObj);
      }

      return nestedObj;
    });

    return objs;
  } catch {
    return objs;
  }
}

export const getSearchParam = (param: string) => {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URL(window.location.href).searchParams;

  return params.get(param);
};
